import { render } from '@testing-library/react';
import React, { ReactNode, useMemo } from 'react';
import { ErrorsContext } from '../contexts/errors';
import { GenericsContext } from '../contexts/generics';
import { InputsContext } from '../contexts/inputs';
import { LiferayContext } from '../contexts/liferay';
import { SearchContext } from '../contexts/search';
import {
  ErrorsContextProps,
  GenericsContextProps,
  IErrorsState,
  IGenericsState,
  IInputsState,
  ILiferayState,
  ISearchState,
  InputsContextProps,
  LiferayContextProps,
  SearchContextProps,
} from '../types';

type ContextMockState = {
  errors: IErrorsState;
  generics: IGenericsState;
  inputs: IInputsState;
  liferay: ILiferayState;
  search: ISearchState;
};

type UpdatesMock = {
  errors: ErrorsContextProps['updateErrorsState'];
  generics: GenericsContextProps['updateGenericsState'];
  inputs: InputsContextProps['updateInputsState'];
  liferay: LiferayContextProps['updateLiferayState'];
  search: SearchContextProps['updateSearchState'];
};

export const renderWithContext = (
  children: ReactNode,
  mockState: ContextMockState | Record<string, any> = {},
): ReturnType<typeof render> => {
  const updatesMock: UpdatesMock = {
    generics: { isMobile: jest.fn() },
    errors: {
      datesError: { dropoff: jest.fn(), pickup: jest.fn() },
      locationError: { dropoff: jest.fn(), oneway: jest.fn(), pickup: jest.fn() },
      timeError: { dropoff: jest.fn(), pickup: jest.fn() },
      reset: jest.fn(),
    },
    liferay: { antecedenceTime: { international: jest.fn(), national: jest.fn() } },
    inputs: {
      closeAllInputs: jest.fn(),
      openDates: jest.fn(),
      openTimeModal: jest.fn(),
      openLocationDropoff: jest.fn(),
      openTimeDropoff: jest.fn(),
      openTimePickup: jest.fn(),
    },
    search: {
      dates: { dropoff: jest.fn(), pickup: jest.fn() },
      location: { dropoff: jest.fn(), pickup: jest.fn() },
      time: { dropoff: jest.fn(), pickup: jest.fn() },
      differentAgencies: jest.fn(),
    },
  };

  const Wrapper = () => {
    const memoizedContexts = useMemo(
      () => ({
        generics: { genericsState: mockState.generics, updateGenericsState: updatesMock.generics },
        liferay: { liferayState: mockState.liferay, updateLiferayState: updatesMock.liferay },
        inputs: { inputsState: mockState.inputs, updateInputsState: updatesMock.inputs },
        search: { searchState: mockState.search, updateSearchState: updatesMock.search },
        errors: { errorsState: mockState.errors, updateErrorsState: updatesMock.errors },
      }),
      [mockState, updatesMock],
    );

    return (
      <GenericsContext.Provider value={memoizedContexts.generics}>
        <LiferayContext.Provider value={memoizedContexts.liferay}>
          <InputsContext.Provider value={memoizedContexts.inputs}>
            <SearchContext.Provider value={memoizedContexts.search}>
              <ErrorsContext.Provider value={memoizedContexts.errors}>{children}</ErrorsContext.Provider>
            </SearchContext.Provider>
          </InputsContext.Provider>
        </LiferayContext.Provider>
      </GenericsContext.Provider>
    );
  };

  return render(<Wrapper />);
};

/**
 * Esta função executa uma função passada por parâmetro, sempre que o usuário clicar fora do componente informado pela REF, ou pressionar a tecla ESCAPE(ESC)
 * @param ref Informe o referência do elemento HTML
 * @param event Informe a função que será executada
 */
export const handleClosePopUp = (ref: React.RefObject<any>, event: Function) => {
  const handleClickOutside = ({ target }: Event) => {
    if (ref.current && !ref.current.contains(target as Node)) {
      event();
    }
  };

  document.addEventListener('click', handleClickOutside, true);

  return () => {
    document.removeEventListener('click', handleClickOutside, true);
  };
};
